import { Table, Thead, Tbody, Tr, Th, Box, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { TellerSettingsView } from 'types/teller';

import { useRefreshTellerInstitutionMutation } from 'borrower/domains/bankIntegrations/requests/teller.gql.generated';
import { AccountSelectionHeader } from 'borrower/domains/bankIntegrations/ui/teller/AccountSelectionHeader';
import { AccountSelectionRow } from 'borrower/domains/bankIntegrations/ui/teller/AccountSelectionRow';
import {
  GetBankAccountsByCreditFacilityIdDocumentGQLType,
  GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
} from 'packages/bankAccounts/requests/bankAccounts.gql.generated';
import { colors } from 'packages/common-frontend/colors';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';

export const Institution = ({
  institutionWithAccounts,
  disconnectAccount,
  disconnectAccountIsLoading,
  cashBalancePreference,
}: TellerSettingsView.InstitutionProps) => {
  const toast = useToast();

  const [
    refreshAccounts,
    { loading: refreshAccountsIsLoading },
  ] = useRefreshTellerInstitutionMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    refetchQueries: [
      GetBankAccountsByCreditFacilityIdDocumentGQLType,
      GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
    ],
  });

  const { bankAccounts } = institutionWithAccounts;

  return (
    <Sentry.ErrorBoundary
      onError={() => {
        if (!toast.isActive(errorToastId)) {
          errorToastConfig.title = 'Error selecting institution accounts';
          toast(errorToastConfig);
        }
      }}
    >
      <Box display="flex" flexDirection="column" mb="15px">
        <AccountSelectionHeader
          institutionWithAccounts={institutionWithAccounts}
          disconnectAccount={disconnectAccount}
          disconnectAccountIsLoading={disconnectAccountIsLoading}
          refreshAccounts={refreshAccounts}
          refreshAccountsIsLoading={refreshAccountsIsLoading}
        />

        <Table fontSize="sm" variant="simple">
          <Thead
            background={colors.tableHeader}
            borderBottom="1px solid lightgrey"
          >
            <Tr>
              <Th w="sm">Name</Th>
              <Th w="xs">Type</Th>
              <Th w="xs">{`${cashBalancePreference}`}</Th>
              <Th w="xs">Currency</Th>
              <Th w="xs">Last Refresh</Th>
            </Tr>
          </Thead>
          <Tbody>
            {bankAccounts.map((bankAccount) => {
              return (
                <AccountSelectionRow
                  cashBalancePreference={cashBalancePreference}
                  key={bankAccount.dataProviderAccountId}
                  institutionAccount={bankAccount}
                  institutionWithAccounts={institutionWithAccounts}
                  isLoading={refreshAccountsIsLoading}
                />
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Sentry.ErrorBoundary>
  );
};
